import React, { useState, useMemo, useRef, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { tokens } from '../../utils/theme';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine
} from 'recharts';
import { Tooltip as MuiTooltip } from "@mui/material";
import EconomicBatteriesModal from './EconomicBatteriesModal';

const Container = styled.div`
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'radial-gradient(circle at top left, rgba(130, 170, 255, 0.05), rgba(12, 14, 23, 0.95))'
        : 'radial-gradient(circle at top left, rgba(15,118,110,0.04), rgba(255,255,255,0.95))'};
    border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
    border-radius: 14px;
    padding: 20px 20px 0 20px;
    color: ${props => props.theme.palette.text.primary};
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 10px;
    padding-bottom: 24px;
    margin-right: -10px;

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.22);
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.08);
    }
    &::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.26), rgba(128, 203, 196, 0.44));
        border-radius: 999px;
        border: 1px solid rgba(128, 203, 196, 0.20);
    }
    &::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.4), rgba(128, 203, 196, 0.6));
    }
`;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const TopBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
`;

const SelectorGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

const Label = styled.span`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const DropdownWrapper = styled.div`
    position: relative;
    user-select: none;
    min-width: 230px;
`;

const DropdownTrigger = styled.button`
    width: 100%;
    background: ${props => props.theme.palette.mode === 'dark' ? '#0d1117' : '#f8fafc'};
    border: 1px solid ${props => props.$isOpen
        ? '#80cbc4'
        : (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.22)' : 'rgba(15, 23, 42, 0.12)')};
    border-radius: 6px;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
    font-size: 12px;
    font-weight: 600;
    padding: 7px 12px;
    outline: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    transition: all 0.2s ease;
    box-shadow: ${props => props.$isOpen ? '0 0 0 2px rgba(128, 203, 196, 0.15)' : 'none'};

    &:hover {
        border-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.45)' : 'rgba(15, 118, 110, 0.35)'};
    }
`;

const TriggerContent = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
`;

const TriggerText = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: 0.02em;
`;

const TriggerCode = styled.span`
    font-size: 10px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 3px;
    background: rgba(128, 203, 196, 0.12);
    color: #80cbc4;
    border: 1px solid rgba(128, 203, 196, 0.24);
    letter-spacing: 0.04em;
`;

const ChevronIcon = styled.svg`
    width: 12px;
    height: 12px;
    fill: none;
    stroke: ${props => props.$isOpen ? '#80cbc4' : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(15, 23, 42, 0.5)')};
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: transform 0.2s ease, stroke 0.2s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    flex-shrink: 0;
`;

const DropdownMenu = styled.div`
    position: absolute;
    top: calc(100% + 5px);
    left: 0;
    min-width: 100%;
    width: 260px;
    max-height: 280px;
    overflow-y: auto;
    background: ${props => props.theme.palette.mode === 'dark' ? '#0d1117' : '#ffffff'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.25)' : 'rgba(15, 23, 42, 0.15)'};
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.65), 0 8px 10px -6px rgba(0, 0, 0, 0.4);
    padding: 4px;
    z-index: 1000;

    &::-webkit-scrollbar {
        width: 5px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(128, 203, 196, 0.3);
        border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(128, 203, 196, 0.55);
    }
`;

const DropdownItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 10px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 12px;
    font-weight: ${props => props.$selected ? '600' : '500'};
    color: ${props => props.$selected ? '#80cbc4' : (props.theme.palette.mode === 'dark' ? '#c9d1d9' : '#1e293b')};
    background: ${props => props.$selected
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.12)' : 'rgba(15, 118, 110, 0.08)')
        : 'transparent'};
    transition: background 0.12s ease, color 0.12s ease;

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.08)' : 'rgba(0, 0, 0, 0.04)'};
        color: ${props => props.theme.palette.mode === 'dark' ? '#ffffff' : '#0b1220'};
    }
`;

const ItemInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
`;

const ItemName = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const ItemCode = styled.span`
    font-size: 10px;
    font-weight: 700;
    padding: 1px 4px;
    border-radius: 3px;
    color: ${props => props.$selected ? '#80cbc4' : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(15, 23, 42, 0.45)')};
    background: ${props => props.$selected
        ? 'rgba(128, 203, 196, 0.15)'
        : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.05)')};
`;

const CheckIcon = styled.svg`
    width: 12px;
    height: 12px;
    stroke: #80cbc4;
    stroke-width: 2.5;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
`;

const HeaderCard = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.6)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
    border-radius: 8px;
    padding: 12px 16px;
`;

const CountryInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const CountryTitle = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
`;

const SubText = styled.span`
    font-size: 11px;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const StatusBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 4px;
    background: ${props => props.$bg || 'rgba(128, 203, 196, 0.12)'};
    color: ${props => props.$color || '#80cbc4'};
    border: 1px solid ${props => props.$border || 'transparent'};
`;

const HeartbeatButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    cursor: pointer;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.12)' : 'rgba(15, 118, 110, 0.08)'};
    color: #80cbc4;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.3)' : 'rgba(15, 118, 110, 0.22)'};
    transition: all 0.15s ease;

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.22)' : 'rgba(15, 118, 110, 0.15)'};
        border-color: #80cbc4;
        color: #ffffff;
        transform: translateY(-1px) scale(1.05);
        box-shadow: 0 4px 14px rgba(128, 203, 196, 0.25);
    }

    &:active {
        transform: scale(0.96);
    }
`;


const IndicatorsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
    }
    @media (max-width: 700px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const MetricCard = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.6)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
    border-radius: 8px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const MetricLabel = styled.span`
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const MetricVal = styled.span`
    font-size: 20px;
    font-weight: 700;
    color: ${props => props.$color || (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220')};
    font-variant-numeric: tabular-nums;
`;

const DualGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const SectionCard = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.6)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SectionTitle = styled.h4`
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${props => tokens(props.theme.palette.mode).grey[300]};
`;

const TimeframeBtn = styled.button`
    background: ${props => props.$active ? 'rgba(128, 203, 196, 0.18)' : 'transparent'};
    border: 1px solid ${props => props.$active ? '#80cbc4' : 'rgba(255, 255, 255, 0.12)'};
    color: ${props => props.$active ? '#80cbc4' : tokens(props.theme.palette.mode).grey[300]};
    font-size: 11px;
    font-weight: 600;
    padding: 5px 10px;
    min-height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        color: #fff;
    }
`;

const LegendPill = styled.button`
    background: ${props => props.$active ? (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)') : 'transparent'};
    border: 1px solid ${props => props.$active ? (props.$color || 'rgba(128, 203, 196, 0.3)') : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.$active ? (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220') : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(15, 23, 42, 0.4)')};
    border-radius: 4px;
    padding: 0 8px;
    height: 26px;
    width: 64px;
    box-sizing: border-box;
    font-size: 11px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: ${props => props.$clickable ? 'pointer' : 'default'};
    transition: all 0.15s ease;
    line-height: 1;

    &:hover {
        border-color: ${props => props.$clickable ? (props.$color || '#80cbc4') : (props.$color || 'rgba(128, 203, 196, 0.3)')};
    }
`;

const LegendDot = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${props => props.$color || '#80cbc4'};
    display: inline-block;
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 7px 0;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.04)'};
    font-size: 12px;

    &:last-child {
        border-bottom: none;
    }
`;

const DetailLabel = styled.span`
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const DetailValue = styled.span`
    font-weight: 600;
    color: ${props => props.$color || (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220')};
`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDate = (val) => {
    if (!val) return 'N/A';
    let year, month, day;
    if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
        const parts = val.split('T')[0].split('-').map(Number);
        year = parts[0];
        month = parts[1] - 1;
        day = parts[2];
    } else {
        const parsed = new Date(val);
        if (!isNaN(parsed.getTime())) {
            year = parsed.getFullYear();
            month = parsed.getMonth();
            day = parsed.getDate();
        }
    }

    if (year === undefined) return val;

    const monthStr = MONTHS[month] || '';
    const currentYear = new Date().getFullYear();

    if (year === currentYear || year === 2026) {
        return `${monthStr} ${day}`;
    }
    return `${monthStr} ${day}, ${year}`;
};

const BpBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 3px;
    margin-left: 6px;
    font-variant-numeric: tabular-nums;
    background: ${props => props.$isCut
        ? 'rgba(239, 83, 80, 0.12)'
        : (props.$isHike ? 'rgba(38, 166, 154, 0.12)' : 'rgba(255, 255, 255, 0.08)')};
    color: ${props => props.$isCut
        ? '#ef5350'
        : (props.$isHike ? '#80cbc4' : props.theme.palette.text.secondary)};
    border: 1px solid ${props => props.$isCut
        ? 'rgba(239, 83, 80, 0.28)'
        : (props.$isHike ? 'rgba(38, 166, 154, 0.28)' : 'rgba(255, 255, 255, 0.12)')};
`;


const ValMain = styled.span`
    font-size: 12.5px;
    font-weight: 700;
    color: ${props => props.$color || (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220')};
    font-variant-numeric: tabular-nums;
`;

const ValSub = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: ${props => props.$color || tokens(props.theme.palette.mode).grey[400]};
    margin-left: 6px;
    font-variant-numeric: tabular-nums;
`;

const InflationItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.05)'};

    &:last-child {
        border-bottom: none;
        padding-bottom: 2px;
    }

    &:first-of-type {
        padding-top: 4px;
    }
`;

const ItemTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
    letter-spacing: 0.01em;
`;

const YoYText = styled.span`
    font-weight: 400;
    font-style: italic;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    font-size: 11.5px;
    margin-left: 4px;
`;

const ItemSubtitle = styled.div`
    font-size: 11px;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    margin-top: 3px;
    max-width: 320px;
    line-height: 1.35;
`;

const ItemSubRight = styled.div`
    font-size: 11px;
    font-weight: 500;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    margin-top: 3px;
    font-variant-numeric: tabular-nums;
`;

const parseLastChangeParts = (str, cb) => {
    if (!str) return { dateStr: 'N/A', bpStr: null, isCut: false, isHike: false };

    const match = str.match(/^([A-Za-z]{3}\s+\d{1,2}(?:,\s+\d{4})?)\s*(?:\(([^)]+)\))?$/);
    let datePart = str;
    let bpPart = null;

    if (match) {
        datePart = match[1];
        bpPart = match[2];
    }

    const formattedDate = formatDate(datePart);
    let isCut = cb?.direction === 'cut' || (cb?.bpsChange !== undefined && cb.bpsChange < 0);
    let isHike = cb?.direction === 'hike' || (cb?.bpsChange !== undefined && cb.bpsChange > 0);
    let displayBp = null;

    if (bpPart) {
        const numMatch = bpPart.match(/(-?\+?\d+)\s*(?:bp|pts)?/i);
        if (numMatch) {
            const num = parseInt(numMatch[1], 10);
            if (num < 0 || isCut) {
                displayBp = `-${Math.abs(num)}bp`;
                isCut = true;
                isHike = false;
            } else if (num > 0 || isHike) {
                displayBp = `+${Math.abs(num)}bp`;
                isHike = true;
                isCut = false;
            } else {
                displayBp = `${num}bp`;
            }
        } else {
            displayBp = bpPart;
        }
    } else if (cb?.bpsChange !== undefined && cb.bpsChange !== 0) {
        if (cb.bpsChange < 0) {
            displayBp = `${cb.bpsChange}bp`;
            isCut = true;
        } else {
            displayBp = `+${cb.bpsChange}bp`;
            isHike = true;
        }
    }

    return { dateStr: formattedDate, bpStr: displayBp, isCut, isHike };
};

const HISTORICAL_RATE_MAP = {
    PBOC: {
        "2021-01": 3.85, "2021-07": 3.85, "2022-01": 3.70, "2022-07": 3.70,
        "2023-01": 3.65, "2023-07": 3.55, "2024-01": 3.45, "2024-07": 3.35,
        "2025-01": 3.10, "2025-07": 3.10, "2026-01": 3.00, "2026-06": 3.00, "2026-10": 3.00
    },
    RBI: {
        "2021-01": 4.00, "2021-07": 4.00, "2022-01": 4.00, "2022-07": 4.90,
        "2023-01": 6.25, "2023-07": 6.50, "2024-01": 6.50, "2024-07": 6.50,
        "2025-01": 6.25, "2025-07": 6.00, "2026-01": 5.75, "2026-06": 5.50, "2026-10": 5.50
    }
};

const HISTORICAL_CPI_MAP = {
    FED: {
        "2021-01": 1.40, "2021-07": 5.40, "2022-01": 7.50, "2022-07": 8.50,
        "2023-01": 6.40, "2023-07": 3.20, "2024-01": 3.10, "2024-07": 2.90,
        "2025-01": 2.60, "2025-07": 2.80, "2026-01": 3.20, "2026-06": 3.50, "2026-10": 3.70
    },
    ECB: {
        "2021-01": 0.90, "2021-07": 2.20, "2022-01": 5.10, "2022-07": 8.90,
        "2023-01": 8.60, "2023-07": 5.30, "2024-01": 2.80, "2024-07": 2.60,
        "2025-01": 2.40, "2025-07": 2.20, "2026-01": 2.50, "2026-06": 3.10, "2026-10": 3.80
    },
    BOE: {
        "2021-01": 0.70, "2021-07": 2.00, "2022-01": 5.50, "2022-07": 10.10,
        "2023-01": 10.10, "2023-07": 6.80, "2024-01": 4.00, "2024-07": 2.20,
        "2025-01": 2.50, "2025-07": 2.80, "2026-01": 3.00, "2026-06": 3.30, "2026-10": 3.50
    },
    BOJ: {
        "2021-01": -0.60, "2021-07": -0.30, "2022-01": 0.50, "2022-07": 2.60,
        "2023-01": 4.30, "2023-07": 3.30, "2024-01": 2.20, "2024-07": 2.80,
        "2025-01": 2.50, "2025-07": 2.30, "2026-01": 2.20, "2026-06": 2.10, "2026-10": 2.10
    },
    BOC: {
        "2021-01": 1.00, "2021-07": 3.70, "2022-01": 5.10, "2022-07": 7.60,
        "2023-01": 5.90, "2023-07": 3.30, "2024-01": 2.90, "2024-07": 2.50,
        "2025-01": 2.30, "2025-07": 2.50, "2026-01": 2.70, "2026-06": 2.90, "2026-10": 3.10
    },
    RBA: {
        "2021-01": 0.90, "2021-07": 3.00, "2022-01": 3.50, "2022-07": 6.10,
        "2023-01": 7.80, "2023-07": 6.00, "2024-01": 4.10, "2024-07": 3.80,
        "2025-01": 3.50, "2025-07": 3.20, "2026-01": 3.00, "2026-06": 3.40, "2026-10": 3.60
    },
    SNB: {
        "2021-01": -0.50, "2021-07": 0.70, "2022-01": 1.60, "2022-07": 3.40,
        "2023-01": 3.30, "2023-07": 1.60, "2024-01": 1.30, "2024-07": 1.30,
        "2025-01": 1.10, "2025-07": 1.00, "2026-01": 1.00, "2026-06": 1.10, "2026-10": 1.10
    },
    PBOC: {
        "2021-01": 0.30, "2021-07": 1.00, "2022-01": 0.90, "2022-07": 2.50,
        "2023-01": 2.10, "2023-07": -0.30, "2024-01": -0.80, "2024-07": 0.50,
        "2025-01": 0.30, "2025-07": 0.60, "2026-01": 0.70, "2026-06": 0.80, "2026-10": 0.90
    },
    RBI: {
        "2021-01": 4.06, "2021-07": 5.59, "2022-01": 6.01, "2022-07": 6.71,
        "2023-01": 6.52, "2023-07": 7.44, "2024-01": 5.10, "2024-07": 3.60,
        "2025-01": 4.80, "2025-07": 5.10, "2026-01": 5.40, "2026-06": 5.50, "2026-10": 5.54
    }
};

export default function CountryHealthTab({ data, selectedCountryId, onSelectCountry }) {
    const theme = useTheme();
    const countries = useMemo(() => data?.countries || [], [data?.countries]);
    const centralBanks = useMemo(() => data?.centralBanks || [], [data?.centralBanks]);
    const historicalRates = useMemo(() => data?.historicalRates || [], [data?.historicalRates]);

    const [activeId, setActiveId] = useState(selectedCountryId || 'US');
    const [rateTimeframe, setRateTimeframe] = useState('5Y');
    const [showInflation, setShowInflation] = useState(true);
    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const [isBatteryModalOpen, setIsBatteryModalOpen] = useState(false);
    const selectRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsSelectOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsSelectOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    React.useEffect(() => {
        if (selectedCountryId) {
            setActiveId(selectedCountryId);
        }
    }, [selectedCountryId]);

    const country = useMemo(() => {
        return countries.find(c => c.id === activeId) || countries.find(c => c.id === 'US') || countries[0] || {};
    }, [countries, activeId]);

    const centralBank = useMemo(() => {
        if (!country.centralBankKey) return centralBanks[0] || {};
        return centralBanks.find(cb => cb.key === country.centralBankKey) || {};
    }, [centralBanks, country]);

    const handleSelect = (id) => {
        setActiveId(id);
        if (onSelectCountry) onSelectCountry(id);
    };

    const rateHistory = useMemo(() => {
        if (!historicalRates.length || !country.centralBankKey) return [];
        let list = historicalRates;
        if (rateTimeframe === '1Y') list = historicalRates.slice(-3);
        else if (rateTimeframe === '2Y') list = historicalRates.slice(-5);
        else if (rateTimeframe === '5Y') list = historicalRates.slice(-10);

        const cbKey = country.centralBankKey;
        const cpiKey = `${cbKey}_CPI`;

        return list.map(pt => {
            let rateVal = pt[cbKey];
            if (rateVal === undefined && HISTORICAL_RATE_MAP[cbKey]) {
                rateVal = HISTORICAL_RATE_MAP[cbKey][pt.date];
            }
            if (rateVal === undefined) {
                rateVal = country.interestRate;
            }

            let inflationVal = pt[cpiKey];
            if (inflationVal === undefined && HISTORICAL_CPI_MAP[cbKey]) {
                inflationVal = HISTORICAL_CPI_MAP[cbKey][pt.date];
            }
            if (inflationVal === undefined) {
                inflationVal = country.inflationRate;
            }

            return {
                date: pt.date,
                rate: rateVal,
                inflation: inflationVal,
                realRate: rateVal !== undefined && inflationVal !== undefined ? Number((rateVal - inflationVal).toFixed(2)) : null
            };
        });
    }, [historicalRates, country, rateTimeframe]);

    const growthColor = country.gdpGrowth > 0
        ? tokens(theme.palette.mode).greenAccent[500]
        : (country.gdpGrowth < 0 ? tokens(theme.palette.mode).redAccent[500] : tokens(theme.palette.mode).grey[300]);

    const inflationColor = country.inflationRate > 4
        ? tokens(theme.palette.mode).redAccent[500]
        : (country.inflationRate <= 2.5 ? tokens(theme.palette.mode).greenAccent[500] : '#ffb74d');

    const budgetColor = country.govBudget >= 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500];
    const realRateColor = country.realInterestRate > 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500];

    return (
        <Container>
            <ScrollableContent>
                <Stack>
                    {/* Top Selector Bar */}
                    <TopBar>
                        <SelectorGroup>
                            <Label>Country:</Label>
                            <DropdownWrapper ref={selectRef}>
                                <DropdownTrigger
                                    type="button"
                                    $isOpen={isSelectOpen}
                                    onClick={() => setIsSelectOpen(prev => !prev)}
                                    aria-haspopup="listbox"
                                    aria-expanded={isSelectOpen}
                                >
                                    <TriggerContent>
                                        <TriggerText>{country.name || 'Select a country'}</TriggerText>
                                        {country.code && <TriggerCode>{country.code}</TriggerCode>}
                                    </TriggerContent>
                                    <ChevronIcon $isOpen={isSelectOpen} viewBox="0 0 24 24">
                                        <polyline points="6 9 12 15 18 9" />
                                    </ChevronIcon>
                                </DropdownTrigger>

                                {isSelectOpen && (
                                    <DropdownMenu role="listbox">
                                        {countries.map(c => {
                                            const isSelected = c.id === country.id;
                                            return (
                                                <DropdownItem
                                                    key={c.id}
                                                    role="option"
                                                    aria-selected={isSelected}
                                                    $selected={isSelected}
                                                    onClick={() => {
                                                        handleSelect(c.id);
                                                        setIsSelectOpen(false);
                                                    }}
                                                >
                                                    <ItemInfo>
                                                        <ItemName>{c.name}</ItemName>
                                                    </ItemInfo>
                                                    <ItemMeta>
                                                        {c.code && <ItemCode $selected={isSelected}>{c.code}</ItemCode>}
                                                        {isSelected && (
                                                            <CheckIcon viewBox="0 0 24 24">
                                                                <polyline points="20 6 9 17 4 12" />
                                                            </CheckIcon>
                                                        )}
                                                    </ItemMeta>
                                                </DropdownItem>
                                            );
                                        })}
                                    </DropdownMenu>
                                )}
                            </DropdownWrapper>
                        </SelectorGroup>
                    </TopBar>

                    {/* Country Overview Line */}
                    <HeaderCard>
                        <CountryInfo>
                            <CountryTitle>{country.name}</CountryTitle>
                            <StatusBadge>{country.code}</StatusBadge>
                            <SubText>• {country.centralBank}</SubText>
                        </CountryInfo>

                        <MuiTooltip title="Economic Health" arrow enterDelay={150} leaveDelay={100}>
                            <HeartbeatButton
                                type="button"
                                onClick={() => setIsBatteryModalOpen(true)}
                                aria-label="Economic Health"
                            >
                            <svg
                                viewBox="0 0 24 24"
                                width="20"
                                height="20"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M3.5 12 C2.5 7.5 5.5 4 9 4 C 11 4 12 5.5 12 7 C 12 5.5 13 4 15 4 C 18.5 4 21.5 7.5 20.5 12" />
                                <path d="M4.5 14.5 L12 21.5 L19.5 14.5" />
                                <path d="M1 12 L6.5 12 L9 6 L12.5 16.5 L15.5 8.5 L17.5 12 L23 12" />
                            </svg>
                        </HeartbeatButton>
                        </MuiTooltip>
                    </HeaderCard>

                    {/* 10 Core Metrics Grid */}
                    <IndicatorsGrid>
                        <MetricCard>
                            <MetricLabel>GDP ($B)</MetricLabel>
                            <MetricVal>${country.gdp?.toLocaleString()}</MetricVal>
                        </MetricCard>

                        <MetricCard>
                            <MetricLabel>GDP Growth</MetricLabel>
                            <MetricVal $color={growthColor}>
                                {country.gdpGrowth > 0 ? `+${country.gdpGrowth?.toFixed(2)}%` : `${country.gdpGrowth?.toFixed(2)}%`}
                            </MetricVal>
                        </MetricCard>

                        <MetricCard>
                            <MetricLabel>Interest Rate</MetricLabel>
                            <MetricVal $color="#80cbc4">
                                {country.interestRate?.toFixed(2)}%
                            </MetricVal>
                        </MetricCard>

                        <MetricCard>
                            <MetricLabel>Inflation (CPI)</MetricLabel>
                            <MetricVal $color={inflationColor}>
                                {country.inflationRate?.toFixed(2)}%
                            </MetricVal>
                        </MetricCard>

                        <MetricCard>
                            <MetricLabel>Real Rate</MetricLabel>
                            <MetricVal $color={realRateColor}>
                                {country.realInterestRate > 0 ? `+${country.realInterestRate?.toFixed(2)}%` : `${country.realInterestRate?.toFixed(2)}%`}
                            </MetricVal>
                        </MetricCard>

                        <MetricCard>
                            <MetricLabel>Jobless Rate</MetricLabel>
                            <MetricVal $color={country.joblessRate > 6 ? '#ef5350' : undefined}>
                                {country.joblessRate?.toFixed(2)}%
                            </MetricVal>
                        </MetricCard>

                        <MetricCard>
                            <MetricLabel>Gov. Budget</MetricLabel>
                            <MetricVal $color={budgetColor}>
                                {country.govBudget > 0 ? `+${country.govBudget?.toFixed(2)}%` : `${country.govBudget?.toFixed(2)}%`}
                            </MetricVal>
                        </MetricCard>

                        <MetricCard>
                            <MetricLabel>Debt / GDP</MetricLabel>
                            <MetricVal $color={country.debtToGdp > 100 ? '#ef5350' : undefined}>
                                {country.debtToGdp?.toFixed(1)}%
                            </MetricVal>
                        </MetricCard>

                        <MetricCard>
                            <MetricLabel>Current Account</MetricLabel>
                            <MetricVal $color={country.currentAccount >= 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500]}>
                                {country.currentAccount > 0 ? `+${country.currentAccount?.toFixed(2)}%` : `${country.currentAccount?.toFixed(2)}%`}
                            </MetricVal>
                        </MetricCard>

                        <MetricCard>
                            <MetricLabel>Population</MetricLabel>
                            <MetricVal>{country.population?.toFixed(1)} M</MetricVal>
                        </MetricCard>
                    </IndicatorsGrid>

                    {/* Row 1: Central Bank Details & Rate History Chart */}
                    <DualGrid>
                        {/* Central Bank Info */}
                        <SectionCard style={{ height: '100%' }}>
                            <SectionHeader>
                                <SectionTitle>Central Bank — {centralBank.key || country.centralBankKey}</SectionTitle>
                                <StatusBadge>{centralBank.stance || 'Neutral'}</StatusBadge>
                            </SectionHeader>

                            <DetailRow>
                                <DetailLabel>Institution</DetailLabel>
                                <DetailValue>{centralBank.name || country.centralBank}</DetailValue>
                            </DetailRow>

                            <DetailRow>
                                <DetailLabel>Current Rate</DetailLabel>
                                <DetailValue $color="#80cbc4" style={{ fontSize: '14px' }}>
                                    {country.interestRate?.toFixed(2)}%
                                </DetailValue>
                            </DetailRow>

                            <DetailRow>
                                <DetailLabel>Next Meeting</DetailLabel>
                                <DetailValue>
                                    {formatDate(centralBank.nextMeetingIso || centralBank.nextMeeting)}
                                </DetailValue>
                            </DetailRow>

                            {(() => {
                                const lastChangeInfo = parseLastChangeParts(centralBank.lastChange, centralBank);
                                return (
                                    <DetailRow>
                                        <DetailLabel>Last Decision</DetailLabel>
                                        <DetailValue>
                                            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                <span>{lastChangeInfo.dateStr}</span>
                                                {lastChangeInfo.bpStr && (
                                                    <BpBadge $isCut={lastChangeInfo.isCut} $isHike={lastChangeInfo.isHike}>
                                                        {lastChangeInfo.bpStr}
                                                    </BpBadge>
                                                )}
                                            </div>
                                        </DetailValue>
                                    </DetailRow>
                                );
                            })()}
                        </SectionCard>

                        {/* Policy Rate vs Inflation Chart (Facing Central Bank) */}
                        <SectionCard style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <SectionHeader>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <SectionTitle>Policy Rate vs Inflation</SectionTitle>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <LegendPill
                                            type="button"
                                            $active={true}
                                            $color="#80cbc4"
                                            $clickable={false}
                                        >
                                            <LegendDot $color="#80cbc4" />
                                            <span>Rate</span>
                                        </LegendPill>
                                        <LegendPill
                                            type="button"
                                            $active={showInflation}
                                            $color="#ffb74d"
                                            $clickable={true}
                                            onClick={() => setShowInflation(prev => !prev)}
                                            title="Click to toggle CPI curve"
                                        >
                                            <LegendDot $color="#ffb74d" />
                                            <span>CPI</span>
                                        </LegendPill>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    {['1Y', '2Y', '5Y', 'MAX'].map(tf => (
                                        <TimeframeBtn
                                            key={tf}
                                            $active={rateTimeframe === tf}
                                            onClick={() => setRateTimeframe(tf)}
                                        >
                                            {tf}
                                        </TimeframeBtn>
                                    ))}
                                </div>
                            </SectionHeader>

                            <div style={{ width: '100%', flex: 1, minHeight: '190px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={rateHistory}
                                        margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                                    >
                                        <XAxis
                                            dataKey="date"
                                            stroke="rgba(255, 255, 255, 0.15)"
                                            tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 10 }}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            stroke="rgba(255, 255, 255, 0.15)"
                                            tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 10 }}
                                            tickLine={false}
                                            domain={['auto', 'auto']}
                                            unit="%"
                                        />
                                        <ReferenceLine y={0} stroke="rgba(255, 255, 255, 0.1)" />
                                        <Tooltip
                                            cursor={{ stroke: 'rgba(128, 203, 196, 0.35)', strokeDasharray: '3 3' }}
                                            content={({ active, payload, label }) => {
                                                if (active && payload && payload.length) {
                                                    const dataPoint = payload[0]?.payload || {};
                                                    const rateVal = dataPoint.rate;
                                                    const inflationVal = dataPoint.inflation;
                                                    const realRate = rateVal !== undefined && inflationVal !== undefined ? (rateVal - inflationVal).toFixed(2) : null;
                                                    return (
                                                        <div style={{
                                                            background: theme.palette.mode === 'dark' ? '#0d1117' : '#fff',
                                                            border: '1px solid rgba(128, 203, 196, 0.3)',
                                                            borderRadius: '6px',
                                                            padding: '8px 12px',
                                                            fontSize: '11px',
                                                            boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
                                                        }}>
                                                            <div style={{ color: tokens(theme.palette.mode).grey[400], marginBottom: '4px', fontWeight: 600 }}>{label}</div>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', margin: '2px 0' }}>
                                                                <span style={{ color: '#80cbc4', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                                                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#80cbc4' }} />
                                                                    Rate:
                                                                </span>
                                                                <span style={{ fontWeight: 700, color: '#e6edf7' }}>{Number(rateVal).toFixed(2)}%</span>
                                                            </div>
                                                            {showInflation && inflationVal !== undefined && (
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', margin: '2px 0' }}>
                                                                    <span style={{ color: '#ffb74d', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                                                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ffb74d' }} />
                                                                        CPI:
                                                                    </span>
                                                                    <span style={{ fontWeight: 700, color: '#e6edf7' }}>{Number(inflationVal).toFixed(2)}%</span>
                                                                </div>
                                                            )}
                                                            {showInflation && realRate !== null && (
                                                                <div style={{
                                                                    marginTop: '6px',
                                                                    paddingTop: '6px',
                                                                    borderTop: '1px solid rgba(255,255,255,0.08)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    gap: '14px'
                                                                }}>
                                                                    <span style={{ color: tokens(theme.palette.mode).grey[400], fontSize: '10px' }}>Real Rate (Spread):</span>
                                                                    <span style={{ fontWeight: 700, fontSize: '10.5px', color: Number(realRate) > 0 ? '#80cbc4' : '#ef5350' }}>
                                                                        {Number(realRate) > 0 ? `+${realRate}%` : `${realRate}%`}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="rate"
                                            name="Rate"
                                            stroke="#80cbc4"
                                            strokeWidth={2}
                                            dot={{ r: 2.5, fill: '#80cbc4' }}
                                            isAnimationActive={false}
                                        />
                                        {showInflation && (
                                            <Line
                                                type="monotone"
                                                dataKey="inflation"
                                                name="CPI"
                                                stroke="#ffb74d"
                                                strokeWidth={2}
                                                dot={{ r: 3, fill: '#ffb74d' }}
                                                isAnimationActive={false}
                                            />
                                        )}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </SectionCard>
                    </DualGrid>

                    {/* Row 2: Dual Mandate (Inflation & Labor Market) */}
                    <DualGrid>
                        {/* Inflation Dynamics */}
                        <SectionCard style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <div>
                                <SectionHeader style={{ marginBottom: '4px' }}>
                                    <SectionTitle>Inflation</SectionTitle>
                                    <StatusBadge
                                        $color={country.inflationRate > 4 ? '#ef5350' : (country.inflationRate <= 2.5 ? '#80cbc4' : '#ffb74d')}
                                        $bg={country.inflationRate > 4 ? 'rgba(239, 83, 80, 0.12)' : (country.inflationRate <= 2.5 ? 'rgba(38, 166, 154, 0.12)' : 'rgba(255, 183, 77, 0.12)')}
                                    >
                                        {country.inflationTrend || (country.inflationRate > 3 ? 'Above Target' : 'Anchored')}
                                    </StatusBadge>
                                </SectionHeader>

                                <InflationItem>
                                    <div>
                                        <ItemTitle>CPI <YoYText>— YoY</YoYText></ItemTitle>
                                        <ItemSubtitle>Headline benchmark</ItemSubtitle>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <ValMain style={{ fontSize: '18px' }}>
                                            {country.inflationRate !== undefined ? `${country.inflationRate.toFixed(2)}%` : '3.70%'}
                                        </ValMain>
                                        <ItemSubRight>
                                            Forecast: {country.inflationForecast !== undefined ? `${country.inflationForecast.toFixed(2)}%` : '3.75%'}
                                        </ItemSubRight>
                                    </div>
                                </InflationItem>

                                <InflationItem>
                                    <div>
                                        <ItemTitle>
                                            {country.id === 'US' ? 'Core PCE' : 'Core Inflation'} <YoYText>— YoY</YoYText>
                                        </ItemTitle>
                                        <ItemSubtitle>
                                            {country.id === 'US'
                                                ? "Fed's preferred gauge"
                                                : "Target benchmark"}
                                        </ItemSubtitle>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        {(() => {
                                            const gaugeVal = country.id === 'US'
                                                ? (country.corePceForecast !== undefined ? country.corePceForecast : 3.13)
                                                : (country.coreInflationForecast !== undefined ? country.coreInflationForecast : country.coreInflationRate);
                                            const targetVal = country.inflationTarget !== undefined ? country.inflationTarget : 2.00;
                                            const val = gaugeVal !== undefined && gaugeVal !== null ? gaugeVal : 3.13;
                                            const isAbove = val > targetVal;
                                            return (
                                                <>
                                                    <ValMain style={{ fontSize: '18px', color: isAbove ? '#ef5350' : '#80cbc4' }}>
                                                        {val.toFixed(2)}%
                                                    </ValMain>
                                                    <ItemSubRight>Target: {targetVal.toFixed(2)}%</ItemSubRight>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </InflationItem>
                            </div>
                        </SectionCard>

                        {/* Labor Market */}
                        <SectionCard style={{ height: '100%' }}>
                            <SectionHeader>
                                <SectionTitle>Labor Market</SectionTitle>
                                <StatusBadge
                                    $color={country.joblessRate > 6 ? '#ef5350' : (country.joblessRate <= 4.2 ? '#80cbc4' : '#ffb74d')}
                                    $bg={country.joblessRate > 6 ? 'rgba(239, 83, 80, 0.12)' : (country.joblessRate <= 4.2 ? 'rgba(38, 166, 154, 0.12)' : 'rgba(255, 183, 77, 0.12)')}
                                >
                                    {country.laborStance || (country.joblessRate <= 4.5 ? 'Healthy Normalization' : 'Moderate Slack')}
                                </StatusBadge>
                            </SectionHeader>

                            <DetailRow>
                                <DetailLabel>{country.id === 'US' ? 'Non-Farm Payrolls (NFP)' : 'Employment Change'}</DetailLabel>
                                <DetailValue>
                                    {country.id === 'US' ? (
                                        <>
                                            <ValMain>+{country.nfpActual || 185}k</ValMain>
                                            <ValSub>• vs +{country.nfpForecast || 165}k</ValSub>
                                        </>
                                    ) : (
                                        <>
                                            <ValMain>{country.employmentChange || '+25k'}</ValMain>
                                            {country.employmentChangeForecast && (
                                                <ValSub>• vs {country.employmentChangeForecast}</ValSub>
                                            )}
                                        </>
                                    )}
                                </DetailValue>
                            </DetailRow>

                            <DetailRow>
                                <DetailLabel>Unemployment Rate (U-3)</DetailLabel>
                                <DetailValue>
                                    <ValMain $color={country.joblessRate > 6 ? '#ef5350' : (country.joblessRate <= 4.2 ? '#80cbc4' : '#ffb74d')}>
                                        {country.joblessRate !== undefined ? `${country.joblessRate.toFixed(2)}%` : 'N/A'}
                                    </ValMain>
                                    <ValSub>• vs {country.joblessRateForecast ? `${country.joblessRateForecast.toFixed(2)}%` : (country.id === 'US' ? '4.30%' : `${country.joblessRate ? country.joblessRate.toFixed(2) : '4.20'}%`)}</ValSub>
                                </DetailValue>
                            </DetailRow>

                            <DetailRow>
                                <DetailLabel>{country.id === 'US' ? 'Job Openings (JOLTS)' : 'Job Vacancies'}</DetailLabel>
                                <DetailValue>
                                    <ValMain>{country.id === 'US' ? (country.joltsOpenings ? `${country.joltsOpenings}M` : '7.67M') : (country.jobVacancies || '2.6%')}</ValMain>
                                    {country.id === 'US' ? (
                                        <ValSub>• vs {country.joltsForecast ? `${country.joltsForecast}M` : '7.65M'}</ValSub>
                                    ) : (
                                        country.jobVacanciesForecast && <ValSub>• vs {country.jobVacanciesForecast}</ValSub>
                                    )}
                                </DetailValue>
                            </DetailRow>

                            <DetailRow>
                                <DetailLabel>{country.id === 'US' ? 'Wage Growth (AHE)' : 'Wage Growth'}</DetailLabel>
                                <DetailValue>
                                    <ValMain>+{country.wageGrowthAheYoY || 3.80}% YoY</ValMain>
                                    {country.wageGrowthAheMoM !== undefined && (
                                        <ValSub>(+{country.wageGrowthAheMoM.toFixed(2)}% MoM)</ValSub>
                                    )}
                                </DetailValue>
                            </DetailRow>
                        </SectionCard>
                    </DualGrid>

                </Stack>
            </ScrollableContent>

            <EconomicBatteriesModal
                isOpen={isBatteryModalOpen}
                onClose={() => setIsBatteryModalOpen(false)}
                country={country}
            />
        </Container>
    );
}
